import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Container,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  SelectChangeEvent,
} from '@mui/material';
import { ArrowBack, Save, Cancel } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import JoditEditor from '../../components/JoditEditor';
import { centerServiceInstructionsService } from './centerServiceInstructions.service';
import { diagnosticCentersService } from '../diagnosticCenters/diagnosticCenters.service';
import { servicesService } from '../services/services.service';
import { DiagnosticCenter } from '../diagnosticCenters/diagnosticCenters.dto';
import { Service } from '../services/services.dto';

const AddCenterServiceInstruction = () => {
  const navigate = useNavigate();
  const { token, logout } = useAuth();

  const [formData, setFormData] = useState({
    diagnosticCenterId: '',
    serviceId: '',
    about_examination: '',
    preparation_instructions: '',
    directions: '',
    ground_level: '',
    parking: '',
    rates: '',
    how_to_pay: '',
    drop_off: '',
    ttc: '',
  });
  const [centers, setCenters] = useState<DiagnosticCenter[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  useEffect(() => {
    centerServiceInstructionsService.setLogoutCallback(logout);
    if (!token) return;
    diagnosticCentersService
      .getDiagnosticCenters(token, { limit: 500 })
      .then(res => setCenters(res.data || []))
      .catch(() => undefined);
    servicesService
      .getServices(token, { limit: 100 })
      .then(res =>
        // Inactive/unavailable services shouldn't be offered here -
        // instructions can't sensibly be attached to a service that isn't
        // actually bookable.
        setServices(
          (res.data || []).filter(
            service => service.status === 1 && service.isAvailable === 1
          )
        )
      )
      .catch(() => undefined);
  }, [token, logout]);

  const handleSelectChange =
    (field: 'diagnosticCenterId' | 'serviceId') =>
    (event: SelectChangeEvent) => {
      setFormData({ ...formData, [field]: event.target.value });
      if (errors[field]) setErrors({ ...errors, [field]: '' });
    };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.diagnosticCenterId)
      newErrors.diagnosticCenterId = 'Diagnostic center is required';
    if (!formData.serviceId) newErrors.serviceId = 'Service is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm() || !token) return;
    setLoading(true);
    try {
      await centerServiceInstructionsService.createInstruction(token, {
        diagnosticCenterId: Number(formData.diagnosticCenterId),
        serviceId: Number(formData.serviceId),
        about_examination: formData.about_examination,
        preparation_instructions: formData.preparation_instructions,
        directions: formData.directions,
        ground_level: formData.ground_level,
        parking: formData.parking,
        rates: formData.rates,
        how_to_pay: formData.how_to_pay,
        drop_off: formData.drop_off,
        ttc: formData.ttc,
      });
      setSnackbar({
        open: true,
        message: 'Instructions created successfully',
        severity: 'success',
      });
      setTimeout(() => navigate('/center-service-instructions'), 800);
    } catch (err) {
      setSnackbar({
        open: true,
        message:
          err instanceof Error ? err.message : 'Failed to create instructions',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const textFields: Array<{ key: keyof typeof formData; label: string }> = [
    { key: 'about_examination', label: 'About the Examination' },
    { key: 'preparation_instructions', label: 'Preparation Instructions' },
    { key: 'directions', label: 'Directions' },
    { key: 'ground_level', label: 'Ground Level' },
    { key: 'parking', label: 'Parking' },
    { key: 'rates', label: 'Rates' },
    { key: 'how_to_pay', label: 'How to Pay' },
    { key: 'drop_off', label: 'Drop Off' },
    { key: 'ttc', label: 'TTC' },
  ];

  return (
    <Container maxWidth="lg">
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)}>
          Back
        </Button>
        <Typography variant="h4">Add Center Service Instructions</Typography>
      </Box>

      <Card>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.diagnosticCenterId}>
                <InputLabel>Diagnostic Center</InputLabel>
                <Select
                  value={formData.diagnosticCenterId}
                  label="Diagnostic Center"
                  onChange={handleSelectChange('diagnosticCenterId')}
                >
                  {centers.map(center => (
                    <MenuItem key={center.id} value={center.id}>
                      {center.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.serviceId}>
                <InputLabel>Service</InputLabel>
                <Select
                  value={formData.serviceId}
                  label="Service"
                  onChange={handleSelectChange('serviceId')}
                >
                  {services.map(service => (
                    <MenuItem key={service.id} value={service.id}>
                      {service.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {textFields.map(({ key, label }) => (
              <Grid item xs={12} key={key}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {label}
                </Typography>
                <JoditEditor
                  value={formData[key]}
                  onChange={value => setFormData({ ...formData, [key]: value })}
                  placeholder={`${label}...`}
                  height={200}
                />
              </Grid>
            ))}

            <Grid item xs={12}>
              <Box display="flex" gap={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={() => navigate(-1)}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={
                    loading ? <CircularProgress size={18} /> : <Save />
                  }
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  Save
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AddCenterServiceInstruction;
